import React, { useState } from "react";
import { Trans, useLingui } from "@lingui/react/macro";
import { useLanguageStore } from "../../stores/languageStore.ts";
import {
  BookingChangeRequestResponseDto,
  useUpdateChangeRequestMutation,
  useCancelChangeRequestMutation,
} from "../../api/bookingChangeRequestApi.ts";
import { format } from "date-fns";
import { useNavigate } from "react-router";
import {
  ArrowRight,
  CalendarClock,
  Check,
  Loader2,
  MessageSquare,
  X,
} from "lucide-react";
import ChangeRequestExpiry from "./ChangeRequestExpiry.tsx";

interface PendingChangeRequestProps {
  changeRequest: BookingChangeRequestResponseDto;
  bookingId: string;
  currentUserId: string;
}

const PendingChangeRequest: React.FC<PendingChangeRequestProps> = ({
  changeRequest,
  bookingId,
  currentUserId,
}) => {
  const { t } = useLingui();
  const { dateFnsLocale } = useLanguageStore();
  const navigate = useNavigate();

  const [showRejectForm, setShowRejectForm] = useState(false);
  const [responseMessage, setResponseMessage] = useState("");

  const { mutate: updateChangeRequest, isPending: isUpdating } =
    useUpdateChangeRequestMutation();
  const { mutate: cancelChangeRequest, isPending: isCancelling } =
    useCancelChangeRequestMutation();

  const isRequester = changeRequest.requestedBy === currentUserId;

  const formatTimeslot = (from: string, to: string): string => {
    const fromDate = new Date(from);
    const toDate = new Date(to);
    return `${format(fromDate, "EEE MMM d, HH:mm", { locale: dateFnsLocale })} - ${format(toDate, "HH:mm", { locale: dateFnsLocale })}`;
  };

  const handleAccept = () => {
    updateChangeRequest({
      bookingId,
      changeRequestId: changeRequest.id,
      dto: { status: "ACCEPTED" },
    });
  };

  const handleReject = () => {
    updateChangeRequest(
      {
        bookingId,
        changeRequestId: changeRequest.id,
        dto: {
          status: "REJECTED",
          responseMessage: responseMessage.trim() || undefined,
        },
      },
      {
        onSuccess: () => {
          setShowRejectForm(false);
          setResponseMessage("");
        },
      },
    );
  };

  const handleCancel = () => {
    cancelChangeRequest({
      bookingId,
      changeRequestId: changeRequest.id,
    });
  };

  const handleSuggestDifferentTimes = () => {
    navigate(
      `/user/${currentUserId}/bookings/${bookingId}/move?counter=${changeRequest.id}`,
    );
  };

  // Requester view: waiting for response
  if (isRequester) {
    return (
      <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4">
        <div className="mb-3 flex items-center gap-2">
          <CalendarClock className="h-5 w-5 text-yellow-600" />
          <h3 className="text-sm font-semibold text-yellow-800">
            <Trans>Move Request Pending</Trans>
          </h3>
        </div>

        <p className="mb-3 text-sm text-yellow-700">
          <Trans>
            You&apos;ve requested to move this booking. Waiting for response.
          </Trans>
        </p>

        {changeRequest.message && (
          <div className="mb-3 flex items-start gap-1.5 text-sm text-yellow-700">
            <MessageSquare className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span className="italic">{changeRequest.message}</span>
          </div>
        )}

        {/* Timeslot changes */}
        <div className="mb-4 space-y-2">
          {changeRequest.timeslotChanges.map((change, index) => (
            <div
              key={index}
              className="flex flex-col gap-1 rounded-md bg-yellow-100/50 px-3 py-2 text-xs sm:flex-row sm:items-center sm:gap-2"
            >
              <span className="text-yellow-700">
                {formatTimeslot(change.originalFrom, change.originalTo)}
              </span>
              <ArrowRight className="hidden h-3.5 w-3.5 shrink-0 text-yellow-500 sm:block" />
              <span className="font-medium text-yellow-800">
                {formatTimeslot(change.proposedFrom, change.proposedTo)}
              </span>
            </div>
          ))}
        </div>

        <ChangeRequestExpiry
          autoCancelAt={changeRequest.autoCancelAt}
          className="mb-3 text-yellow-700"
        />

        <button
          onClick={handleCancel}
          disabled={isCancelling}
          className="inline-flex items-center gap-1.5 rounded-md border border-yellow-300 bg-white px-3 py-1.5 text-sm font-medium text-yellow-800 transition-colors hover:bg-yellow-100 disabled:opacity-50"
        >
          {isCancelling ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <X className="h-4 w-4" />
          )}
          <Trans>Cancel Request</Trans>
        </button>
      </div>
    );
  }

  // Responder view: can accept, reject, or counter-propose
  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 p-4">
      <div className="mb-3 flex items-center gap-2">
        <CalendarClock className="h-5 w-5 text-amber-600" />
        <h3 className="text-sm font-semibold text-amber-800">
          <Trans>Move Request</Trans>
        </h3>
      </div>

      <p className="mb-2 text-sm text-amber-700">
        <Trans>
          {changeRequest.requestedByName} has requested to move this booking.
        </Trans>
      </p>

      {changeRequest.message && (
        <div className="mb-3 flex items-start gap-1.5 text-sm text-amber-700">
          <MessageSquare className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span className="italic">{changeRequest.message}</span>
        </div>
      )}

      {/* Timeslot change comparison */}
      <div className="mb-4 space-y-2">
        {changeRequest.timeslotChanges.map((change, index) => (
          <div
            key={index}
            className="flex flex-col gap-1 rounded-md bg-amber-100/60 px-3 py-2 text-xs sm:flex-row sm:items-center sm:gap-2"
          >
            <span className="text-amber-700">
              {formatTimeslot(change.originalFrom, change.originalTo)}
            </span>
            <ArrowRight className="hidden h-3.5 w-3.5 shrink-0 text-amber-500 sm:block" />
            <span className="font-medium text-amber-900">
              {formatTimeslot(change.proposedFrom, change.proposedTo)}
            </span>
          </div>
        ))}
      </div>

      <ChangeRequestExpiry
        autoCancelAt={changeRequest.autoCancelAt}
        className="mb-4 text-amber-700"
      />

      {/* Reject form */}
      {showRejectForm && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3">
          <label className="mb-1.5 block text-sm font-medium text-red-800">
            <Trans>Reason for rejection (optional)</Trans>
          </label>
          <textarea
            value={responseMessage}
            onChange={(e) => setResponseMessage(e.target.value)}
            placeholder={t`Let them know why you're declining...`}
            className="mb-2 w-full rounded-md border border-red-200 bg-white px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:border-red-400 focus:ring-1 focus:ring-red-300 focus:outline-none"
            rows={2}
          />
          <div className="flex gap-2">
            <button
              onClick={handleReject}
              disabled={isUpdating}
              className="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-red-700 disabled:opacity-50"
            >
              {isUpdating ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <X className="h-4 w-4" />
              )}
              <Trans>Confirm Reject</Trans>
            </button>
            <button
              onClick={() => {
                setShowRejectForm(false);
                setResponseMessage("");
              }}
              className="rounded-md px-3 py-1.5 text-sm text-red-700 transition-colors hover:bg-red-100"
            >
              <Trans>Cancel</Trans>
            </button>
          </div>
        </div>
      )}

      {/* Action buttons */}
      {!showRejectForm && (
        <div className="flex flex-wrap gap-2">
          <button
            onClick={handleAccept}
            disabled={isUpdating}
            className="inline-flex items-center gap-1.5 rounded-md bg-green-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-green-700 disabled:opacity-50"
          >
            {isUpdating ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Check className="h-4 w-4" />
            )}
            <Trans>Accept</Trans>
          </button>

          <button
            onClick={() => setShowRejectForm(true)}
            className="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-red-700"
          >
            <X className="h-4 w-4" />
            <Trans>Reject</Trans>
          </button>

          {changeRequest.timeslotChanges.length === 1 && (
            <button
              onClick={handleSuggestDifferentTimes}
              className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-blue-700"
            >
              <CalendarClock className="h-4 w-4" />
              <Trans>Suggest Different Times</Trans>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default PendingChangeRequest;
